import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// Período: selector de mes (en español) + año. Almacena "MM/YYYY".
export function MonthYearField({ value, onChange, testid }) {
  const valid = /^(\d{2})\/(\d{4})$/.test(String(value || ""));
  const [mm, yyyy] = valid ? value.split("/") : ["", ""];
  const cur = new Date().getFullYear();
  const years = [];
  for (let y = cur + 1; y >= cur - 8; y--) years.push(String(y));
  if (yyyy && !years.includes(yyyy)) years.push(yyyy);

  const setMonth = (m) => onChange(`${m}/${yyyy || String(cur)}`);
  const setYear = (y) => onChange(`${mm || "01"}/${y}`);

  return (
    <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid={testid}>
      <Select value={mm} onValueChange={setMonth}>
        <SelectTrigger className="rounded-none" data-testid={`${testid}-mes`}><SelectValue placeholder="Mes" /></SelectTrigger>
        <SelectContent>
          {MESES.map((nombre, i) => <SelectItem key={i} value={String(i + 1).padStart(2, "0")}>{nombre}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={yyyy} onValueChange={setYear}>
        <SelectTrigger className="rounded-none" data-testid={`${testid}-anio`}><SelectValue placeholder="Año" /></SelectTrigger>
        <SelectContent>
          {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// Fecha: calendario en español (date-fns es). Muestra dd/MM/yyyy.
// Si iso=true, value y onChange usan "YYYY-MM-DD" (para el motor de cálculo); si no, "DD/MM/YYYY".
export function DateField({ value, onChange, testid, iso = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const storeFmt = iso ? "yyyy-MM-dd" : "dd/MM/yyyy";
  const parsed = value ? parse(value, storeFmt, new Date()) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid}
          className={`justify-start rounded-none font-normal h-10 ${className}`}>
          <CalendarIcon className="mr-2 h-4 w-4 opacity-60" />
          {selected ? format(selected, "dd/MM/yyyy") : <span className="text-muted-foreground">dd/mm/aaaa</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" locale={es} selected={selected} defaultMonth={selected}
          onSelect={(d) => { onChange(d ? format(d, storeFmt) : ""); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}
